import { CALENDARS, CLOSES, CLOSE_SCALE, NAMES, RECORDS } from './dataset';
import { ISSUERS } from './issuers';
import { decodeVarints, fromBase64 } from '../prices/varint';
import { expandKeys } from '../keys';
import { tierOf, type Tier } from '../tiers';
import { kindOf } from '../distributions/kinds';
import { nameOf } from '../holdings/names';
import { allocationKindOf } from '../allocations/kinds';
import { nameOfPlace } from '../lookups/countries';
import { sectorName } from '../lookups/sectors';
import { nameOfExchange } from '../lookups/exchanges';
import { issuerId } from '../lookups/issuers';
import { sectionName } from '../lookups/sections';
import { riskLabel, type RiskCode } from '../lookups/risk';
import { frequencyOf } from '../lookups/frequency';
import { hedgeLabel } from '../lookups/hedging';
import { reasonText } from '../reasons';
import { DEFAULTS } from '../defaults';
import { UNITS, PERCENT, AMOUNT, ALLOCATION } from './scales';

/**
 * Turn the two shipped files back into records.
 *
 * Decoded once, lazily, on first read. Everything above this line is codes and
 * integers; everything below it is the shapes the app has always worked with,
 * so nothing downstream knows the artefact exists.
 *
 * Lazy on purpose: a page that only needs one fund's price should not pay to
 * expand 1,379 holdings. The blob is read in one pass because varint has no
 * random access, but the records are built on demand.
 */

export interface Holding {
    symbol: string | null;
    name: string;
    weight: number;
    sector: string | null;
    country: string | null;
}

export interface Distribution {
    exDate: string;
    payDate: string | null;
    amount: number;
    kind: string;
    frequency: string | null;
    isCash: boolean;
    currency: string;
}

export interface TaxYear {
    year: number;
    eligibleDividends: number;
    capitalGains: number;
    otherIncome: number;
    foreignIncome: number;
    foreignTaxWithheld: number;
    returnOfCapital: number;
    totalPerUnit: number;
}

export interface Fund {
    symbol: string;
    tier: Tier;
    issuer: string | null;
    section: string | null;
    mandate: string | null;
    reason: string | null;
    name: string | null;
    exchange: string | null;
    inceptionDate: string | null;
    assets: number | null;
    assetsCurrency: string | null;
    annualizedYield: number | null;
    distributionFrequency: string | null;
    managementFee: number | null;
    feesPassThrough: boolean;
    riskRating: string | null;
    hedging: string | null;
    currency: string;
    asOf: string | null;
    dates: string[];
    closes: Float64Array;
    holdings: Holding[];
    allocations: Record<string, { name: string; weight: number }[]>;
    distributions: Distribution[];
    tax: TaxYear[];
}

const EPOCH = Date.UTC(2000, 0, 1);
const dateOf = (day: number) => new Date(EPOCH + day * 86_400_000).toISOString().slice(0, 10);

const undelta = (values: Int32Array, from: number, count: number) => {
    const out = new Int32Array(count);
    let run = 0;
    for (let i = 0; i < count; i++) out[i] = run += values[from + i];
    return out;
};

type Raw = Record<string, unknown>;

let cache: { funds: Record<string, Fund>; order: string[] } | null = null;

const build = () => {
    if (cache) return cache;

    const total = CALENDARS.reduce((n, c) => n + c.sessions, 0) + RECORDS.reduce((n, r) => n + (((r as Raw).len as number) ?? 0), 0);
    const values = decodeVarints(fromBase64(CLOSES), total);

    // Calendars open the blob, in the order the header lists them.
    let at = 0;
    const calendars: Record<string, string[]> = {};
    for (const { market, sessions } of CALENDARS) {
        const days = undelta(values, at, sessions);
        at += sessions;
        calendars[market] = Array.from(days, dateOf);
    }

    const funds: Record<string, Fund> = {};
    const order: string[] = [];

    for (const record of RECORDS) {
        const r = expandKeys<Raw>(record as Raw);
        const symbol = r.symbol as string;
        order.push(symbol);

        const count = (r.len as number) ?? 0;
        const closeFrom = at;
        at += count;

        const market = (r.market as string) ?? 'CA';
        const gaps: number[] = (r.gaps as number[]) ?? [];
        const sessions = calendars[market] ?? [];

        // The series is the tail of its calendar: as many sessions as it has
        // closes, plus the ones it skipped.
        const span = count + gaps.length;
        const start = sessions.length - span;
        const skipped = gaps.length > 0 ? new Set(gaps) : null;

        const dates: string[] = new Array(count);
        for (let offset = 0, taken = 0; offset < span; offset++) {
            if (skipped?.has(offset)) continue;
            dates[taken++] = sessions[start + offset];
        }

        const cents = count > 0 ? undelta(values, closeFrom, count) : new Int32Array(0);
        const closes = new Float64Array(count);
        for (let i = 0; i < count; i++) closes[i] = cents[i] / CLOSE_SCALE;

        funds[symbol] = buildFund(r, symbol, dates, closes);
    }

    cache = { funds, order };
    return cache;
};

const buildFund = (r: Raw, symbol: string, dates: string[], closes: Float64Array): Fund => {
    const currency = (r.currency as string) ?? DEFAULTS.currency;

    // Holdings. Sector and country are pipe-joined code runs; a row with no
    // ticker carries an index into the unlisted-name table instead.
    const h = (r.holdings as Raw | undefined) ?? {};
    // Weight is the one column always present when there are rows, so it is
    // what says how many there are. The others may have been dropped for
    // holding nothing but nulls or empty codes.
    const hWeights = (h.weight as number[]) ?? [];
    const hSymbols = (h.symbol as (string | null)[]) ?? new Array(hWeights.length).fill(null);
    const hRefs = (h.ref as number[]) ?? [];
    const hSectors = ((h.sector as string) ?? '').split('|');
    const hCountries = ((h.country as string) ?? '').split('|');
    let refAt = 0;

    const holdings: Holding[] = hSymbols.map((sym, i) => ({
        symbol: sym,
        name: nameOf(NAMES, sym, sym && sym in NAMES.bySymbol ? null : hRefs[refAt++]) ?? '',
        weight: hWeights[i] / PERCENT,
        sector: hSectors[i] ? sectorName(hSectors[i]) : null,
        country: hCountries[i] ? nameOfPlace(hCountries[i]) : null,
    }));

    // Allocations. The kind character says how to read the slice's name.
    const a = (r.allocations as Raw | undefined) ?? {};
    const aKinds = (a.kind as string) ?? '';
    const aNames = (a.name as string[]) ?? [];
    const aWeights = (a.weight as number[]) ?? [];
    const allocations: Record<string, { name: string; weight: number }[]> = {};
    for (let i = 0; i < aKinds.length; i++) {
        const meta = allocationKindOf(aKinds[i]);
        (allocations[meta.label] ??= []).push({
            name: meta.places ? nameOfPlace(aNames[i]) : (sectorName(aNames[i]) ?? aNames[i]),
            weight: aWeights[i] / ALLOCATION,
        });
    }

    // Distributions. Frequency and cash come off the kind; the pay date is an
    // offset from the ex-date; both dates and amounts are delta encoded.
    const d = (r.distributions as Raw | undefined) ?? {};
    const dEx = (d.exDate as number[]) ?? [];
    const dGap = (d.payGap as number[]) ?? [];
    const dAmount = (d.amount as number[]) ?? [];
    const dKind = (d.kind as string) ?? '';
    const distributions: Distribution[] = [];
    let day = 0;
    let amount = 0;
    for (let i = 0; i < dEx.length; i++) {
        day += dEx[i];
        amount += dAmount[i];
        const meta = kindOf(dKind[i]);
        distributions.push({
            exDate: dateOf(day),
            payDate: dGap[i] >= 0 ? dateOf(day + dGap[i]) : null,
            amount: amount / AMOUNT,
            kind: meta.label,
            frequency: meta.frequency,
            isCash: meta.cash,
            currency,
        });
    }

    const t = (r.tax as Raw | undefined) ?? {};
    const years = (t.year as number[]) ?? [];
    const component = (key: string, i: number) => (((t[key] as number[]) ?? [])[i] ?? 0) / AMOUNT;
    const tax: TaxYear[] = years.map((year, i) => ({
        year,
        eligibleDividends: component('eligibleDividends', i),
        capitalGains: component('capitalGains', i),
        otherIncome: component('otherIncome', i),
        foreignIncome: component('foreignIncome', i),
        foreignTaxWithheld: component('foreignTaxWithheld', i),
        returnOfCapital: component('returnOfCapital', i),
        totalPerUnit: component('totalPerUnit', i),
    }));

    return {
        symbol,
        tier: tierOf(r.tier as 1 | 0 | undefined),
        issuer: issuerId(r.issuer as string) ?? null,
        section: sectionName(r.section as string) ?? null,
        mandate: (r.mandate as string) ?? null,
        reason: reasonText(r.reason as string),
        name: (r.name as string) ?? null,
        exchange: nameOfExchange(r.exchange as string),
        inceptionDate: (r.inceptionDate as string) ?? null,
        assets: r.assets != null ? (r.assets as number) * UNITS.assets : null,
        assetsCurrency: (r.assetsCurrency as string) ?? null,
        annualizedYield: r.annualizedYield != null ? (r.annualizedYield as number) / PERCENT : null,
        distributionFrequency: frequencyOf(r.distributionFrequency as string),
        managementFee: r.managementFee != null ? (r.managementFee as number) / PERCENT : null,
        feesPassThrough: r.feesPassThrough === true,
        riskRating: riskLabel(r.riskRating as RiskCode),
        hedging: hedgeLabel(r.hedging as string),
        currency,
        asOf: (r.asOf as string) ?? null,
        dates,
        closes,
        holdings,
        allocations,
        distributions,
        tax,
    };
};

export const allFunds = (): Fund[] => {
    const { funds, order } = build();
    return order.map((s) => funds[s]);
};

export const fundOf = (symbol: string): Fund | null => build().funds[symbol] ?? null;

export const fundSymbols = (): string[] => build().order;

export { ISSUERS };
